import { useState, useRef } from "react";

const isMobile = window.innerWidth < 640;

export default function LeadGate({ company, setCompany, onContinue }) {
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [shake, setShake] = useState(false);

  const emailRef = useRef(null);
  const mobileRef = useRef(null);

  // -----------------------------
  // VALIDATION
  // -----------------------------
  const validate = (data = company) => {
    let newErrors = {};

    if (!data.email) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email)) {
      newErrors.email = "Enter a valid email";
    }

    if (!data.mobile) {
      newErrors.mobile = "Mobile is required";
    } else if (!/^[6-9]\d{9}$/.test(data.mobile.replace(/\s/g, ""))) {
      newErrors.mobile = "Enter valid 10-digit mobile";
    }

    setErrors(newErrors);
    return newErrors;
  };

  // -----------------------------
  // FORMAT MOBILE
  // -----------------------------
  const formatMobile = (value) => {
    const digits = value.replace(/\D/g, "").slice(0, 10);
    if (digits.length <= 5) return digits;
    return digits.slice(0, 5) + " " + digits.slice(5);
  };

  // -----------------------------
  // HANDLE CHANGE
  // -----------------------------
  const handleChange = (field, value) => {
    let updatedValue = value;

    if (field === "mobile") {
      updatedValue = formatMobile(value);
    }

    const updated = { ...company, [field]: updatedValue };
    setCompany(updated);
    validate(updated);
  };

  // -----------------------------
  // HANDLE BLUR
  // -----------------------------
  const handleBlur = (field) => {
    setTouched(prev => ({ ...prev, [field]: true }));
    validate();
  };

  // -----------------------------
  // SUBMIT
  // -----------------------------
  const handleContinue = () => {
    const validationErrors = validate();

    if (Object.keys(validationErrors).length === 0) {
      onContinue();
    } else {
      setTouched({ email: true, mobile: true });

      // 🔥 shake animation
      setShake(true);
      setTimeout(() => setShake(false), 400);

      // 🎯 focus first error
      if (validationErrors.email) {
        emailRef.current?.focus();
      } else if (validationErrors.mobile) {
        mobileRef.current?.focus();
      }
    }
  };

  return (
    <div style={styles.overlay}>
      <div
        style={{
          ...styles.modal,
          ...(shake ? styles.shake : {})
        }}
      >
        <h2 style={styles.title}>Unlock Your AI Report 🚀</h2>
        <p style={styles.subtitle}>Enter your details to view full analysis</p>

        {/* EMAIL */}
        <div style={styles.field}>
          <input
            ref={emailRef}
            value={company.email || ""}
            onChange={(e) => handleChange("email", e.target.value)}
            onBlur={() => handleBlur("email")}
            style={{
              ...styles.input,
              ...(errors.email && touched.email ? styles.inputError : {})
            }}
          />
          <label style={styles.label}>Email</label>
          {touched.email && errors.email && (
            <span style={styles.error}>{errors.email}</span>
          )}
        </div>

        {/* MOBILE */}
        <div style={styles.field}>
          <input
            ref={mobileRef}
            value={company.mobile || ""}
            onChange={(e) => handleChange("mobile", e.target.value)}
            onBlur={() => handleBlur("mobile")}
            style={{
              ...styles.input,
              ...(errors.mobile && touched.mobile ? styles.inputError : {})
            }}
          />
          <label style={styles.label}>Mobile</label>
          {touched.mobile && errors.mobile && (
            <span style={styles.error}>{errors.mobile}</span>
          )}
        </div>

        <button style={styles.button} onClick={handleContinue}>
          View Report
        </button>
      </div>
    </div>
  );
}

const styles = {
  overlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.6)",
    display: "flex",
    justifyContent: "center",
    alignItems: isMobile ? "flex-end" : "center",
    padding: isMobile ? "16px" : "20px",
    overflowY: "auto"
  },

  modal: {
    background: "#fff",
    padding: isMobile ? "20px 16px" : 32,
    borderRadius: isMobile ? "20px 20px 0 0" : 20,
    width: "100%",
    maxWidth: 420,
    textAlign: "center",
    boxShadow: "0 20px 60px rgba(0,0,0,0.2)",
    transition: "0.2s",
    marginBottom: isMobile ? 0 : "auto",
  },

  title: {
    fontSize: isMobile ? 20 : 26,
    fontWeight: 700,
    lineHeight: 1.3
  },

  subtitle: {
    color: "#64748b",
    marginBottom: 16,
    fontSize: isMobile ? 13 : 14
  },

  field: {
    position: "relative",
    marginBottom: 20
  },

  input: {
    width: "95%",
    padding: isMobile ? "14px 12px" : "16px 12px",
    borderRadius: 10,
    border: "1px solid #ddd",
    fontSize: isMobile ? 14 : 14,
    outline: "none",
    transition: "0.2s"
  },

  inputError: {
    border: "1px solid #ef4444",
    boxShadow: "0 0 0 2px rgba(239,68,68,0.2)"
  },

  label: {
    position: "absolute",
    top: -8,
    left: 10,
    background: "#fff",
    padding: "0 6px",
    fontSize: 12,
    color: "#64748b"
  },

  error: {
    color: "#ef4444",
    fontSize: 12,
    marginTop: 4,
    display: "block",
    textAlign: "left"
  },

  button: {
    marginTop: 10,
    padding: isMobile ? "14px" : "16px",
    width: "100%",
    borderRadius: 12,
    fontSize: 15,
    border: "none",
    background: "linear-gradient(90deg,#6366f1,#8b5cf6)",
    color: "#fff",
    fontWeight: 600
  },

  shake: {
    animation: "shake 0.3s"
  }
};